import { FormApi } from 'final-form'
import { StudioSystemType } from 'types/global'
import { ReadOnlyLabel } from 'types/mounting'
import { computeFastRackDesignSnow_kPa } from './FastRackComputations'
import { getDlubalCACacheEntry, type DlubalCACacheEntry } from './FastRackDlubal'
import { getSlopeBucket, retrieveAllowableSpan, type Exposure, type MountType } from './FastRackSpanLookup'

const METRES_TO_INCHES = 39.37

export { computeFastRackDesignSnow_kPa } from './FastRackComputations'

function parseOptionalLoad(raw: unknown): number {
  return raw !== undefined && String(raw) !== '' ? Number(raw) : NaN
}

function roundKpa(value: number): number {
  return Math.round(value * 100) / 100
}

function matchesKpaAt2dp(fieldValue: number, reference: number | null | undefined): boolean {
  if (reference === null || reference === undefined || isNaN(fieldValue)) return false
  return roundKpa(fieldValue) === roundKpa(reference)
}

function hasIntegrationSnowZones(integrationData: Record<string, unknown>): boolean {
  return (
    (integrationData.snowSs_kPa !== undefined && String(integrationData.snowSs_kPa).trim() !== '') ||
    (integrationData.snowSr_kPa !== undefined && String(integrationData.snowSr_kPa).trim() !== '')
  )
}

function cacheSnowZones(entry: DlubalCACacheEntry | null): { Ss: number | null; Sr: number | null } {
  if (!entry?.success) return { Ss: null, Sr: null }
  const Ss = entry.snow_zone_1 ?? entry.snow_result ?? null
  if (Ss === null || Ss === undefined) return { Ss: null, Sr: null }
  const Sr = entry.snow_zone_2 ?? 0
  return { Ss, Sr }
}

/**
 * Resolve NBC ground snow Ss and rain-on-snow Sr (kPa) from integration_json and Dlubal cache.
 * Zone values are slope-independent; design S is derived per facet at span/BOM time.
 */
export function resolveFastRackSnowZones(
  integrationData: Record<string, unknown>,
  cacheEntry = getDlubalCACacheEntry('snow')
): { Ss_kPa: number | null; Sr_kPa: number | null } {
  const cached = cacheSnowZones(cacheEntry)
  const userSs = parseOptionalLoad(integrationData.snowSs_kPa)
  const userSr = parseOptionalLoad(integrationData.snowSr_kPa)

  const Ss_kPa = isNaN(userSs)
    ? cached.Ss
    : matchesKpaAt2dp(userSs, cached.Ss)
      ? cached.Ss
      : userSs

  const Sr_kPa = isNaN(userSr)
    ? cached.Sr
    : matchesKpaAt2dp(userSr, cached.Sr)
      ? cached.Sr
      : userSr

  return { Ss_kPa, Sr_kPa }
}

/**
 * Resolve snow (NBC design S, kPa) and wind (q1/50, kPa) for span lookup and BOM.
 *
 * Design snow is computed from Ss/Sr at `slopeDeg`. Legacy `snowLoad` (single design kPa)
 * is honoured when the new zone fields are absent.
 */
export function resolveFastRackSnowWind(
  integrationData: Record<string, unknown>,
  slopeDeg = 20
): { snow_kPa: number | null; wind_q150_kPa: number | null } {
  const { Ss_kPa, Sr_kPa } = resolveFastRackSnowZones(integrationData)
  const userWind = parseOptionalLoad(integrationData.windSpeed)

  const windEntry = getDlubalCACacheEntry('wind')
  const windSnapshot = windEntry?.wind_zone_2 ?? windEntry?.wind_result ?? null

  const wind_q150_kPa = isNaN(userWind)
    ? windSnapshot
    : matchesKpaAt2dp(userWind, windSnapshot)
      ? windSnapshot
      : userWind

  if (!hasIntegrationSnowZones(integrationData)) {
    const legacySnow = parseOptionalLoad(integrationData.snowLoad)
    if (!isNaN(legacySnow)) {
      const snowSnapshot = getDlubalCACacheEntry('snow')?.snow_design_kPa
      const snow_kPa = matchesKpaAt2dp(legacySnow, snowSnapshot)
        ? Ss_kPa !== null
          ? computeFastRackDesignSnow_kPa(Ss_kPa, Sr_kPa ?? 0, slopeDeg)
          : legacySnow
        : legacySnow
      return { snow_kPa, wind_q150_kPa: wind_q150_kPa ?? null }
    }
  }

  const snow_kPa =
    Ss_kPa === null ? null : computeFastRackDesignSnow_kPa(Ss_kPa, Sr_kPa ?? 0, slopeDeg)

  return {
    snow_kPa,
    wind_q150_kPa: wind_q150_kPa ?? null,
  }
}

function getModuleGridLabel(grid: { facet?: unknown; userData?: unknown }, index: number): string {
  const facetFromGrid = grid.facet as { getName?: () => string } | null | undefined
  const facetUuid = (grid.userData as { facetUuid?: string } | undefined)?.facetUuid
  const facet =
    facetFromGrid ??
    (facetUuid && window.editor?.objectByUuid
      ? (window.editor.objectByUuid(facetUuid) as { getName?: () => string } | null)
      : null)
  const facetName = facet?.getName?.()
  if (facetName) return facetName
  return `Grid ${index + 1}`
}

/** Per-facet NBC design snow (kPa) from stored Ss/Sr and each module grid slope. */
export function getFastRackFacetDesignSnowLabels(
  system: StudioSystemType,
  integrationData: Record<string, unknown>
): ReadOnlyLabel[] {
  const { Ss_kPa, Sr_kPa } = resolveFastRackSnowZones(integrationData)
  if (Ss_kPa === null) return []

  const grids = system.moduleGrids?.() ?? []
  return grids.map((grid, index) => {
    const slopeDeg = grid.getSlope?.() ?? 20
    const design_kPa = computeFastRackDesignSnow_kPa(Ss_kPa, Sr_kPa ?? 0, slopeDeg)
    const slopeLabel = Math.round(slopeDeg * 10) / 10
    return {
      label: `${getModuleGridLabel(grid, index)} (${slopeLabel}°)`,
      value: `${roundKpa(design_kPa)} kPa`,
    }
  })
}

/** Module dimension along the horizontal rail direction, in mm. */
export function getFastRackModuleLengthMm(system: StudioSystemType): number | null {
  const grid = system.moduleGrids?.()?.[0]
  const moduleType = grid?.moduleType?.()
  if (!moduleType || !grid) return null

  const dimM = grid.moduleLayout() === 'portrait' ? moduleType.width : moduleType.height
  if (dimM == null || dimM <= 0) return null
  return dimM * 1000
}

export function lookupFastRackTableSpanInches(params: {
  mountType: MountType
  moduleLength_mm: number
  slopeDeg: number
  exposure: Exposure
  snow_kPa: number | null
  wind_q150_kPa: number | null
}): number | null {
  const { mountType, moduleLength_mm, slopeDeg, exposure, snow_kPa, wind_q150_kPa } = params
  if (snow_kPa === null || wind_q150_kPa === null) return null

  const slopeBucket = getSlopeBucket(slopeDeg)
  if (!slopeBucket) return null

  const spanResult = retrieveAllowableSpan({
    mountType,
    panelLength_m: moduleLength_mm / 1000,
    snow_kPa,
    wind_q150: wind_q150_kPa,
    exposure,
    slopeBucket,
  })

  if (!spanResult) return null
  return spanResult.span_m * METRES_TO_INCHES
}

/** Whole-inch span for display and RT-MINI comparison (always round down). */
export function floorSpanInches(span_in: number): number {
  return Math.floor(span_in)
}

export function formatSpanInches(span_in: number): string {
  return String(floorSpanInches(span_in))
}

export function getFastRackCalculatedSpanValue(
  system: StudioSystemType,
  _projectForm: FormApi
): string {
  const integrationData = (system.integration_json?.fastRack ?? {}) as Record<string, unknown>
  const moduleLength_mm = getFastRackModuleLengthMm(system)
  const grid = system.moduleGrids?.()?.[0]
  if (!moduleLength_mm || !grid) return '—'

  const slopeDeg = grid.getSlope?.() ?? 20
  const { snow_kPa, wind_q150_kPa } = resolveFastRackSnowWind(integrationData, slopeDeg)
  const mountType = ((integrationData.mountType as string) || 'SHEATHING') as MountType
  const exposure = ((integrationData.exposure as string) || 'Rough') as Exposure

  const span_in = lookupFastRackTableSpanInches({
    mountType,
    moduleLength_mm,
    slopeDeg,
    exposure,
    snow_kPa,
    wind_q150_kPa,
  })

  return span_in === null ? '—' : formatSpanInches(span_in)
}
